import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaBoxOpen, FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import productService from '../services/productService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, histRes, topCustRes] = await Promise.all([
          productService.getById(id),
          api.get(`/sales/product/${id}`),
          api.get(`/sales/product/${id}/top-customers`)
        ]);
        
        setProduct(prodRes);
        setHistory(histRes.data);
        setTopCustomers(topCustRes.data);
      } catch (error) {
        console.error(error);
        toast.error('Veriler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <div>Yükleniyor...</div>;
  if (!product) return <div>Ürün bulunamadı.</div>;

  // Toplam Satış Miktarı ve Cirosu
  const totalSold = history.reduce((acc, item) => acc + Number(item.quantity), 0);
  const totalRevenue = history.reduce((acc, item) => acc + Number(item.total_price), 0);

  // Grafik Verisi
  const chartData = {
    labels: topCustomers.map(c => c.company_name),
    datasets: [
      {
        label: `Satış Miktarı (${product.unit})`,
        data: topCustomers.map(c => c.total_quantity),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)', // Mavi
          'rgba(255, 99, 132, 0.7)', // Kırmızı
          'rgba(255, 206, 86, 0.7)', // Sarı
          'rgba(75, 192, 192, 0.7)', // Yeşil
          'rgba(153, 102, 255, 0.7)', // Mor
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
        borderRadius: 6,
        barPercentage: 0.7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Yatay bar chart
    animation: {
      duration: 2000,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'En Çok Satın Alan Müşteriler (Top 5)',
        font: { size: 16 },
        padding: { bottom: 20 }
      },
    },
    scales: {
      x: {
        grid: { display: false }
      }
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center mb-4">
        <Link to="/products" className="btn btn-outline-secondary me-3">
          <FaArrowLeft /> Geri
        </Link>
        <h2 className="mb-0">{product.name} - Hareket Geçmişi</h2>
      </div>

      {/* Özet Kartları */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm border-start border-4 border-primary h-100">
            <div className="card-body d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                <FaBoxOpen className="text-primary fs-4" />
              </div>
              <div>
                <div className="text-muted small">Mevcut Stok</div>
                <div className="fs-4 fw-bold">{product.stock_quantity} {product.unit}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm border-start border-4 border-success h-100">
            <div className="card-body d-flex align-items-center">
              <div className="bg-success bg-opacity-10 p-3 rounded-circle me-3">
                <FaShoppingCart className="text-success fs-4" />
              </div>
              <div>
                <div className="text-muted small">Toplam Satılan</div>
                <div className="fs-4 fw-bold">{totalSold} {product.unit}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm border-start border-4 border-warning h-100">
            <div className="card-body d-flex align-items-center">
              <div className="bg-warning bg-opacity-10 p-3 rounded-circle me-3">
                <FaMoneyBillWave className="text-warning fs-4" />
              </div>
              <div>
                <div className="text-muted small">Toplam Satış Cirosu</div>
                <div className="fs-4 fw-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grafik Alanı */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body" style={{ height: '400px' }}>
              {topCustomers.length > 0 ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <p className="text-center text-muted my-4">Henüz satış verisi bulunmuyor.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hareket Tablosu */}
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <h5 className="mb-0">Satış Hareketleri</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Cari Hesap (Müşteri)</th>
                  <th>Satış No</th>
                  <th>Miktar</th>
                  <th>Birim Fiyat</th>
                  <th>Toplam</th>
                  <th>Satışı Yapan</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={index}>
                    <td>{new Date(item.sale_date).toLocaleDateString('tr-TR')}</td>
                    <td>
                      <Link to={`/current-accounts/detail/${item.current_account_id}`} className="text-decoration-none fw-bold">
                        {item.company_name}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/sales/edit/${item.sale_id}`} className="badge bg-secondary text-decoration-none">
                        #{item.sale_id}
                      </Link>
                    </td>
                    <td className="fw-bold">{item.quantity} {item.unit}</td>
                    <td>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.unit_price)}</td>
                    <td className="fw-bold text-success">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.total_price)}</td>
                    <td className="small text-muted">{item.created_by_name || '-'}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">Henüz satış hareketi bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
